/**
 * DICOMweb HTTP client — QIDO-RS for search/metadata, WADO-RS for image
 * retrieval (via Cornerstone's `wadors:` loader). Auth header built from
 * user settings; same `applyAuth(headers)` helper used by the data provider
 * AND by Cornerstone's wadors loader hook so both round-trips agree.
 *
 * Spec refs:
 *   QIDO-RS: PS3.18 §10.6  /studies?StudyDate=...&PatientName=...
 *   WADO-RS: PS3.18 §10.4  /studies/{}/series/{}/instances/{}/frames/{f}
 *   DICOM JSON model: PS3.18 §F  { "00100010": { "vr": "PN", "Value": [...]} }
 */
import { DicomWebAuth, UserSettings } from '../utils/userSettings';

export interface DicomWebConfig {
  /** Base URL — e.g. https://pacs.local/dicom-web. Trailing slash stripped. */
  url: string;
  auth: DicomWebAuth;
  basicUser?: string;
  basicPass?: string;
  bearerToken?: string;
}

export function configFromSettings(s: UserSettings): DicomWebConfig {
  return {
    url: (s.dicomwebUrl || '').replace(/\/+$/, ''),
    auth: s.dicomwebAuth || 'none',
    basicUser: s.dicomwebBasicUser,
    basicPass: s.dicomwebBasicPass,
    bearerToken: s.dicomwebBearerToken,
  };
}

/** Build the Authorization header (or other auth headers) for a request.
 *  Mutates the provided object in place AND returns it for chaining. */
export function applyAuth(headers: Record<string, string>, cfg: DicomWebConfig): Record<string, string> {
  if (cfg.auth === 'basic') {
    const user = cfg.basicUser || '';
    const pass = cfg.basicPass || '';
    headers['Authorization'] = 'Basic ' + btoa(`${user}:${pass}`);
  } else if (cfg.auth === 'bearer' && cfg.bearerToken) {
    headers['Authorization'] = 'Bearer ' + cfg.bearerToken;
  }
  return headers;
}

/** QIDO-RS GET with DICOM JSON Accept. Returns parsed JSON array (one entry
 *  per matched resource — study, series, or instance — depending on the
 *  endpoint). Empty results return []. */
export async function qidoFetch(
  cfg: DicomWebConfig,
  path: string,
  query?: Record<string, string | number | undefined>
): Promise<any[]> {
  const qs = query
    ? '?' + Object.entries(query)
        .filter(([, v]) => v != null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join('&')
    : '';
  const url = `${cfg.url}${path}${qs}`;
  const headers: Record<string, string> = {
    Accept: 'application/dicom+json',
  };
  applyAuth(headers, cfg);

  const res = await fetch(url, { headers });
  if (res.status === 204) return [];  // 204 = no content, valid empty result
  if (!res.ok) {
    throw new Error(`DICOMweb ${res.status} ${res.statusText} on ${path}`);
  }
  return await res.json();
}

/** DICOM JSON tag → friendly key map for the fields we display. Keep this
 *  minimal — adding tags is cheap, but the more we map, the more brittle
 *  the transformer becomes if a server returns unexpected VRs. */
const TAGS = {
  StudyInstanceUID:        '0020000D',
  SeriesInstanceUID:       '0020000E',
  SOPInstanceUID:          '00080018',
  PatientName:             '00100010',
  PatientID:               '00100020',
  PatientBirthDate:        '00100030',
  PatientSex:              '00100040',
  PatientAge:              '00101010',
  StudyDate:               '00080020',
  StudyTime:               '00080030',
  StudyDescription:        '00081030',
  SeriesDescription:       '0008103E',
  AccessionNumber:         '00080050',
  Modality:                '00080060',
  ModalitiesInStudy:       '00080061',
  ReferringPhysicianName:  '00080090',
  InstitutionName:         '00080080',
  StudyID:                 '00200010',
  SeriesNumber:            '00200011',
  NumberOfStudyRelatedInstances:  '00201208',
  NumberOfSeriesRelatedInstances: '00201209',
  BodyPartExamined:        '00180015',
} as const;

/** Extract a single value from a DICOM JSON tag node. The shape is:
 *    { "vr": "PN", "Value": [...] }
 *  For PN (Person Name) it's `[{ Alphabetic: "Smith^John" }]`. For others
 *  it's a plain array. We unwrap to a flat string. */
function tagValue(node: any): string {
  if (!node || !node.Value || node.Value.length === 0) return '';
  const v = node.Value[0];
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (typeof v === 'object' && v !== null) {
    // PN: { Alphabetic: "Last^First^Middle" }
    return v.Alphabetic || v.Ideographic || v.Phonetic || '';
  }
  return '';
}

function tagInt(node: any): number {
  const s = tagValue(node);
  const n = parseInt(s, 10);
  return isNaN(n) ? 0 : n;
}

function tagDate(node: any): string {
  // DICOM dates are YYYYMMDD; reformat to YYYY-MM-DD for our UI.
  const s = tagValue(node);
  if (s.length === 8) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  return s;
}

/** Map a single QIDO study row to the worklist Study shape used by
 *  WorklistPage. seriesCount and key-image columns aren't populated from
 *  QIDO (would need extra round-trips) — they show as 0 / 'none' until
 *  the user opens the study. */
export function qidoStudyToWorklistStudy(s: any): any {
  return {
    studyId: tagValue(s[TAGS.StudyInstanceUID]),  // string UID in DICOMweb mode
    studyInstanceUID:    tagValue(s[TAGS.StudyInstanceUID]),
    patientName:         tagValue(s[TAGS.PatientName]),
    patientId:           tagValue(s[TAGS.PatientID]),
    patientSex:          tagValue(s[TAGS.PatientSex]),
    patientAge:          tagValue(s[TAGS.PatientAge]),
    accessionNumber:     tagValue(s[TAGS.AccessionNumber]),
    studyDate:           tagDate(s[TAGS.StudyDate]),
    modality:            tagValue(s[TAGS.ModalitiesInStudy]) || tagValue(s[TAGS.Modality]),
    studyDescription:    tagValue(s[TAGS.StudyDescription]),
    referringPhysician:  tagValue(s[TAGS.ReferringPhysicianName]),
    institution:         tagValue(s[TAGS.InstitutionName]),
    department:          '',
    readingPhysician:    '',
    performingPhysician: '',
    operator:            '',
    numImages:           tagInt(s[TAGS.NumberOfStudyRelatedInstances]),
    advStatus:           '',
    reportStatus:        'none',
  };
}

/** Map a QIDO series row to our SeriesData shape. Note: sopInstanceUIDs
 *  aren't part of the QIDO /series response — they're fetched separately
 *  via /instances for that series. */
export function qidoSeriesToSeriesData(s: any): {
  seriesInstanceUID: string;
  modality: string;
  seriesNumber: string;
  seriesDescription: string;
  bodyPart: string;
  numImages: number;
} {
  return {
    seriesInstanceUID:   tagValue(s[TAGS.SeriesInstanceUID]),
    modality:            tagValue(s[TAGS.Modality]),
    seriesNumber:        tagValue(s[TAGS.SeriesNumber]),
    seriesDescription:   tagValue(s[TAGS.SeriesDescription]),
    bodyPart:            tagValue(s[TAGS.BodyPartExamined]),
    numImages:           tagInt(s[TAGS.NumberOfSeriesRelatedInstances]),
  };
}

/** Map a QIDO instance row to its SOPInstanceUID. */
export function qidoInstanceToSopUID(i: any): string {
  return tagValue(i[TAGS.SOPInstanceUID]);
}

// ───── Convenience wrappers ─────────────────────────────────────────────

export async function searchStudies(
  cfg: DicomWebConfig,
  filters: Record<string, string | number | undefined>
): Promise<any[]> {
  // QIDO query keyword mapping. Server accepts both DICOM keyword names and
  // tag codes; keywords are more readable in network traces.
  const q: Record<string, string | number | undefined> = {};
  if (filters.patientName)        q.PatientName       = `*${filters.patientName}*`;
  if (filters.patientId)          q.PatientID         = `*${filters.patientId}*`;
  if (filters.accessionNumber)    q.AccessionNumber   = `*${filters.accessionNumber}*`;
  if (filters.modality)           q.ModalitiesInStudy = String(filters.modality);
  if (filters.studyDateFrom && filters.studyDateTo) {
    q.StudyDate = `${filters.studyDateFrom}-${filters.studyDateTo}`;
  } else if (filters.studyDateFrom) {
    q.StudyDate = `${filters.studyDateFrom}-`;
  } else if (filters.studyDateTo) {
    q.StudyDate = `-${filters.studyDateTo}`;
  }
  if (filters.referringPhysician) q.ReferringPhysicianName = `*${filters.referringPhysician}*`;
  if (filters.studyDescription)   q.StudyDescription = `*${filters.studyDescription}*`;
  q.limit  = 500;
  q.offset = 0;
  return qidoFetch(cfg, '/studies', q);
}

export async function getStudySeries(cfg: DicomWebConfig, studyUID: string): Promise<any[]> {
  return qidoFetch(cfg, `/studies/${studyUID}/series`);
}

export async function getSeriesInstances(
  cfg: DicomWebConfig,
  studyUID: string,
  seriesUID: string
): Promise<any[]> {
  return qidoFetch(cfg, `/studies/${studyUID}/series/${seriesUID}/instances`);
}

/**
 * Fetch the FULL DICOM JSON metadata for every instance in a series.
 * Single round-trip; for big series prefer the paged variant below to
 * keep the UI responsive.
 */
export async function getSeriesMetadata(
  cfg: DicomWebConfig,
  studyUID: string,
  seriesUID: string
): Promise<any[]> {
  return qidoFetch(cfg, `/studies/${studyUID}/series/${seriesUID}/metadata`);
}

/**
 * Single page of /metadata. QIDO honors `limit` + `offset`. Returns
 * fewer than `limit` rows when the page exhausts the series.
 */
export async function getSeriesMetadataPage(
  cfg: DicomWebConfig,
  studyUID: string,
  seriesUID: string,
  limit: number,
  offset: number
): Promise<any[]> {
  return qidoFetch(
    cfg,
    `/studies/${studyUID}/series/${seriesUID}/metadata`,
    { limit, offset }
  );
}

/** Lightweight QIDO `/instances` — returns minimum required tags
 *  (SOPInstanceUID, InstanceNumber, SOPClassUID). ~100x smaller per
 *  row than /metadata. We use it to learn the series' instance list
 *  and ordering before kicking off the bigger metadata pages. */
export async function listSeriesInstanceUIDs(
  cfg: DicomWebConfig,
  studyUID: string,
  seriesUID: string
): Promise<Array<{ sopUID: string; instanceNumber: number }>> {
  const rows = await qidoFetch(
    cfg,
    `/studies/${studyUID}/series/${seriesUID}/instances`,
    { limit: 100000 }
  );
  return rows
    .map((r) => ({
      sopUID: r['00080018']?.Value?.[0] || '',
      instanceNumber: Number(r['00200013']?.Value?.[0] || 0),
    }))
    .filter((r) => r.sopUID);
}

/** Build a Cornerstone-compatible WADO-RS imageId. Cornerstone's wadors
 *  loader fetches frames via this URL using fetch + multipart parsing. */
export function buildWadorsImageId(
  cfg: DicomWebConfig,
  studyUID: string,
  seriesUID: string,
  sopInstanceUID: string,
  frame = 1
): string {
  return `wadors:${cfg.url}/studies/${studyUID}/series/${seriesUID}/instances/${sopInstanceUID}/frames/${frame}`;
}

/** Lightweight liveness probe — used by Settings "Test Connection". Any
 *  2xx from /studies?limit=1 counts as success; auth failures bubble up
 *  the HTTP status so the UI can hint at the right fix. */
export async function ping(cfg: DicomWebConfig): Promise<{ ok: boolean; status?: number; error?: string }> {
  try {
    const headers: Record<string, string> = { Accept: 'application/dicom+json' };
    applyAuth(headers, cfg);
    const res = await fetch(`${cfg.url}/studies?limit=1`, { headers });
    if (res.ok || res.status === 204) return { ok: true, status: res.status };
    return { ok: false, status: res.status, error: `${res.status} ${res.statusText}` };
  } catch (e: any) {
    return { ok: false, error: e?.message || 'Network error (CORS? DNS? URL typo?)' };
  }
}
